import React from 'react';
import { Button, Row, InputGroup, FormControl } from 'react-bootstrap';
import { Form, Field, Formik } from 'formik';
import * as yup from 'yup';
import { EMAIL_VALIDATION_REGEX } from '../../../../../../../shared/constants/emailValidation';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import Tooltip from '../../../../../../tooltip';
import RecipientItem from './recipientItem';
import styles from './styles.module.scss';
import { ReactComponent as PlusIcon } from '../../../../../../../shared/icons/plus.svg';

interface AddSignerFormProps {
  onSubmit: (recipients: string[]) => void;
  isCreatingSigner: boolean;
  recipients: string[];
}

const validationSchema = yup.object().shape({
  email: yup.string().trim().required().matches(EMAIL_VALIDATION_REGEX, 'Please provide correct email'),
});

const CC_TOOLTIP_MESSAGE =
  'The final signed copy of this document will be sent to all signers, all deal participants and any emails you specify here';

const AddRecipientForm = ({ recipients, onSubmit, isCreatingSigner }: AddSignerFormProps) => {
  const handleSignersDelete = async (emailToDelete: string) => {
    const filteredRecipientsList = recipients.filter((email: string) => email !== emailToDelete);
    onSubmit(filteredRecipientsList);
  };
  const renderRecipientsItems = () =>
    recipients?.map((signer, index) => {
      return <RecipientItem key={index} email={signer} deleteSigner={handleSignersDelete} />;
    });
  return (
    <Formik
      validateOnBlur
      initialValues={{
        email: '',
      }}
      onSubmit={(values, actions) => {
        onSubmit([...recipients, values.email]);
        actions.resetForm();
      }}
      validationSchema={validationSchema}
    >
      {({ errors, touched, values, handleBlur }) => {
        const isTouchedAndInvalid = !!touched.email && !!errors.email && !!values.email;
        return (
          <div>
            <h1 className={styles.title}>
              Receive Signed Copy (CC)
              <Tooltip testId="cc-form-tooltip" placement="right">
                {CC_TOOLTIP_MESSAGE}
              </Tooltip>
            </h1>
            <Form className={styles.wrapper}>
              <Row className="mx-0 w-100">
                <InputGroup>
                  <FormControl
                    id="addSignerForm.signerEmail"
                    data-testid="signer-email-field"
                    as={Field}
                    name="email"
                    label={<FormattedMessage id="form.name.fullName" defaultMessage="Add Recipient" />}
                    type="text"
                    placeholder="Enter recipients email"
                    isInvalid={isTouchedAndInvalid}
                    onBlur={handleBlur}
                  />
                </InputGroup>
              </Row>
              <Row className={classNames(styles.buttons)}>
                <Button
                  variant="primary"
                  type="submit"
                  className={styles['buttons-add']}
                  disabled={
                    !values.email ||
                    !!errors.email ||
                    isCreatingSigner ||
                    recipients.map((recipient) => recipient.toLowerCase()).includes(values.email.trim().toLowerCase())
                  }
                >
                  <PlusIcon width={16} height={16} />
                </Button>
              </Row>
            </Form>
            <div className="mt-2">{renderRecipientsItems()}</div>
          </div>
        );
      }}
    </Formik>
  );
};

export default AddRecipientForm;
